import { useEffect, useRef } from 'react';

/**
 * useDashboardChime — plays a short two-note chime when the doctor receives
 * a "new lead" or "new patient check-in" push notification, so they hear it
 * even if their system notification sounds are muted but the tab is open.
 *
 * The Service Worker broadcasts a `{type:'docsite_push_event', event, sound}`
 * postMessage on every push receive. This hook listens for those messages
 * and triggers a Web Audio chime (no asset file — generated on the fly).
 *
 * Optionally calls `onArrival(event)` so the dashboard can also refresh its
 * activity counters immediately without waiting for the next 30s poll.
 */
const CHIMING_EVENTS = new Set(['new_connect', 'new_patient_checkin']);

function playChime() {
  try {
    // AudioContext must be created lazily (some browsers block on load).
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const playNote = (freq, start, durMs) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.value = 0;
      gain.gain.linearRampToValueAtTime(0.18, now + start + 0.01);
      gain.gain.linearRampToValueAtTime(0, now + start + (durMs / 1000));
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + (durMs / 1000) + 0.05);
    };
    // Pleasant "two-note rising" chime — 880 Hz then 1318 Hz (A5 → E6).
    playNote(880, 0.0, 180);
    playNote(1318.5, 0.18, 240);
    // Auto-close context to free resources.
    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 800);
  } catch { /* private mode / autoplay-blocked — silent fallback */ }
}

export default function useDashboardChime(onArrival) {
  const onArrivalRef = useRef(onArrival);
  onArrivalRef.current = onArrival;

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const handler = (event) => {
      const d = event?.data;
      if (!d || d.type !== 'docsite_push_event') return;
      if (CHIMING_EVENTS.has(d.event) || d.sound) {
        playChime();
      }
      try { onArrivalRef.current?.(d.event, d.payload); } catch { /* ignore */ }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, []);
}
